import type { PropsWithChildren } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

export const featurePillTones = [
  'primary',
  'secondary',
  'tertiary',
  'quaternary',
  'success',
  'warning',
  'error',
] as const;
export type FeaturePillTone = (typeof featurePillTones)[number];

const toneClasses: Record<FeaturePillTone, string> = {
  primary: 'bg-primary-subtle text-primary-focal border-primary-main/40',
  secondary: 'bg-secondary-subtle text-secondary-focal border-secondary-main/40',
  tertiary: 'bg-tertiary-subtle text-tertiary-emphasized border-tertiary-main/40',
  quaternary: 'bg-quaternary-subtle text-quaternary-emphasized border-quaternary-main/40',
  success: 'bg-success-badge text-success-text border-success-border/40',
  warning: 'bg-warning-badge text-warning-text border-warning-border/40',
  error: 'bg-error-badge text-error-text border-error-border/40',
};

export interface FeaturePillProps extends PropsWithChildren {
  tone?: FeaturePillTone;
  className?: ClassName;
}

/**
 * Informational tag-cloud pill — read-only (unlike `ChipButton`). Used
 * for service-option / atmosphere / dining / parking flags inside the
 * place detail panel: clusters of pre-filled facts about a place.
 *
 * The tone selects which kawaii accent the pill draws from — use the same
 * family across an entire section so a cluster reads as one group.
 */
export const FeaturePill = ({ tone = 'secondary', className, children }: FeaturePillProps) => (
  <span
    className={twx(
      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
      toneClasses[tone],
      className,
    )}
  >
    {children}
  </span>
);
